/**
 * Servicio de identidad del dispositivo
 * Sprint 3 - HU-FE-01: Generar y persistir device_uuid y device_secret
 * 
 * Usa localStorage para almacenamiento
 * NOTA: Para producción móvil, considerar migrar a @capacitor/preferences
 *       o @capacitor-community/secure-storage para mayor seguridad
 */

import { Capacitor } from '@capacitor/core';

// Keys para almacenamiento
const DEVICE_UUID_KEY = 'device_uuid';
const DEVICE_SECRET_KEY = 'device_secret';

// Cache en memoria para evitar lecturas repetidas
let cachedUuid: string | null = null;
let cachedSecret: string | null = null;

/**
 * Genera un UUID v4 seguro
 */
function generateUUID(): string {
    // Usar crypto.randomUUID si está disponible (navegadores modernos)
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }

    // Fallback manual para compatibilidad
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

/**
 * Genera un secret aleatorio seguro (32 bytes en base64)
 */
function generateSecret(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);

    // Convertir a base64 URL-safe
    const base64 = btoa(String.fromCharCode(...array));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

/**
 * Storage adapter - usa localStorage
 * En producción móvil, reemplazar con Capacitor Preferences
 */
const storage = {
    get(key: string): string | null {
        try {
            return localStorage.getItem(key);
        } catch (error) {
            console.warn('⚠️ Error reading from storage:', error);
            return null;
        }
    },

    set(key: string, value: string): void {
        try {
            localStorage.setItem(key, value);
        } catch (error) {
            console.error('❌ Error writing to storage:', error);
        }
    },

    remove(key: string): void {
        try {
            localStorage.removeItem(key);
        } catch (error) {
            console.warn('⚠️ Error removing from storage:', error);
        }
    }
};

/**
 * Inicializa la identidad del dispositivo
 * Genera y persiste uuid/secret si no existen
 */
export async function initializeDeviceIdentity(): Promise<void> {
    // Verificar si ya existe uuid
    let uuid = storage.get(DEVICE_UUID_KEY);
    if (!uuid) {
        uuid = generateUUID();
        storage.set(DEVICE_UUID_KEY, uuid);
        console.log('🔐 Device UUID generado:', uuid);
    }
    cachedUuid = uuid;

    // Verificar si ya existe secret
    let secret = storage.get(DEVICE_SECRET_KEY);
    if (!secret) {
        secret = generateSecret();
        storage.set(DEVICE_SECRET_KEY, secret);
        console.log('🔐 Device Secret generado');
    }
    cachedSecret = secret;
}

/**
 * Obtiene el UUID del dispositivo
 * @throws Error si no se ha inicializado
 */
export function getDeviceUuid(): string {
    if (cachedUuid) return cachedUuid;

    const uuid = storage.get(DEVICE_UUID_KEY);
    if (!uuid) {
        throw new Error('Device identity not initialized. Call initializeDeviceIdentity() first.');
    }
    cachedUuid = uuid;
    return uuid;
}

/**
 * Obtiene el secret del dispositivo
 * @throws Error si no se ha inicializado
 */
export function getDeviceSecret(): string {
    if (cachedSecret) return cachedSecret;

    const secret = storage.get(DEVICE_SECRET_KEY);
    if (!secret) {
        throw new Error('Device identity not initialized. Call initializeDeviceIdentity() first.');
    }
    cachedSecret = secret;
    return secret;
}

/**
 * Obtiene ambos valores de identidad del dispositivo
 */
export function getDeviceIdentity(): { uuid: string; secret: string } {
    return {
        uuid: getDeviceUuid(),
        secret: getDeviceSecret()
    };
}

/**
 * Limpia la identidad del dispositivo (solo para testing/desarrollo)
 */
export function clearDeviceIdentity(): void {
    storage.remove(DEVICE_UUID_KEY);
    storage.remove(DEVICE_SECRET_KEY);
    cachedUuid = null;
    cachedSecret = null;
    console.log('🗑️ Device identity cleared');
}

/**
 * Obtiene el timestamp actual en epoch (segundos)
 */
export function getCurrentTimestamp(): number {
    return Math.floor(Date.now() / 1000);
}

/**
 * Información de la plataforma del dispositivo
 */
export function getDevicePlatform(): 'android' | 'ios' | 'web' {
    if (Capacitor.isNativePlatform()) {
        return Capacitor.getPlatform() as 'android' | 'ios';
    }
    return 'web';
}

